/**
 * The public tournament hub: everything published, narrowed by search, state,
 * division, registration, and timeframe. Filter state lives in the query
 * string, so a coach can send "12U in Texas" to their club as a link.
 */
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isPubliclyVisible } from "@alpha/shared";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { TournamentListItem } from "../lib/types";
import {
  applyFilters,
  availableDivisions,
  availableStates,
  DEFAULT_FILTERS,
  isFiltered,
  queryTerms,
  readFilters,
  readSort,
  sortTournaments,
  writeParams,
  type SortOrder,
  type TournamentFilters,
} from "../lib/tournamentSearch";
import { trackFilterApplied } from "../lib/analytics";
import { TournamentCard } from "../components/TournamentCard";
import { TournamentFilterBar } from "../components/TournamentFilterBar";
import {
  Badge,
  Button,
  ButtonLink,
  EmptyState,
  PageHeader,
  SearchIcon,
  Skeleton,
  stagger,
  TrophyIcon,
} from "../components/ui";

export function Tournaments() {
  const { idToken } = useAuth();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const sort = readSort(params);

  const tournamentsQ = useQuery({
    queryKey: ["public-tournaments"],
    queryFn: () => api.get<{ tournaments: TournamentListItem[] }>("/public/tournaments"),
    staleTime: 60_000,
    retry: 1,
  });

  const events = useMemo(
    () => (tournamentsQ.data?.tournaments ?? []).filter((t) => isPubliclyVisible(t.status)),
    [tournamentsQ.data],
  );
  const matches = useMemo(
    () => sortTournaments(applyFilters(events, filters), sort),
    [events, filters, sort],
  );
  // Facets follow the timeframe, so the dropdowns never offer a state or
  // division that matches nothing in the view the coach is actually looking at
  // — a state with only finished events must not appear under "Upcoming".
  // They deliberately ignore the other axes, so choosing one filter never makes
  // the others disappear out from under you.
  const inTimeframe = useMemo(
    () => applyFilters(events, { ...DEFAULT_FILTERS, timeframe: filters.timeframe }),
    [events, filters.timeframe],
  );
  const states = useMemo(() => availableStates(inTimeframe), [inTimeframe]);
  const divisions = useMemo(() => availableDivisions(inTimeframe), [inTimeframe]);
  const terms = queryTerms(filters.query);

  // The default view is upcoming-only, so an organization whose events have all
  // finished would otherwise land on an empty page with no control to escape it.
  const widerWouldMatch = useMemo(
    () =>
      filters.timeframe !== "all" &&
      applyFilters(events, { ...filters, timeframe: "all" }).length > 0,
    [events, filters],
  );

  const update = (next: TournamentFilters, nextSort: SortOrder) =>
    setParams(writeParams(next, nextSort), { replace: true });
  const clear = () => setParams(new URLSearchParams(), { replace: true });

  const narrowed = isFiltered(filters);

  // What coaches look for, reported once they stop moving. The search box
  // writes a param per keystroke, so firing on the raw change would send one
  // event per letter; a second of quiet stands in for "this is the view I
  // meant". The default, unnarrowed view is already the `/tournaments`
  // pageview, so only a filtered one is worth an event of its own — and the
  // search text itself never leaves the browser, only the fact that there was
  // some. See lib/analytics.ts.
  const resultCount = matches.length;
  useEffect(() => {
    // Waiting on the query is not belt-and-braces: filters come from the URL,
    // so a shared link arrives already narrowed while the list is still empty.
    // Without this the debounce can elapse before the fetch does and report
    // `result_count: 0` for a search that found plenty. `isSuccess` rather
    // than `!isLoading`, so a failed request does not report zero either.
    if (!narrowed || !tournamentsQ.isSuccess) return;
    const timer = setTimeout(
      () =>
        trackFilterApplied({
          state: filters.state,
          division: filters.division,
          registration: filters.registration,
          timeframe: filters.timeframe,
          sort,
          hasQuery: filters.query.trim().length > 0,
          resultCount,
        }),
      1000,
    );
    return () => clearTimeout(timer);
  }, [
    narrowed,
    tournamentsQ.isSuccess,
    filters.state,
    filters.division,
    filters.registration,
    filters.timeframe,
    filters.query,
    sort,
    resultCount,
  ]);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Browse"
        title="Find a tournament"
        subtitle="Search published events by name, division, or location."
        actions={
          idToken ? (
            <ButtonLink variant="secondary" to="/admin">
              Post a tournament
            </ButtonLink>
          ) : undefined
        }
      />

      {events.length > 0 ? (
        <div className="animate-rise" style={stagger(1)}>
          <TournamentFilterBar
            filters={filters}
            sort={sort}
            states={states}
            divisions={divisions}
            onFilters={(next) => update(next, sort)}
            onSort={(nextSort) => update(filters, nextSort)}
            onClear={clear}
          />
        </div>
      ) : null}

      {events.length > 0 ? (
        <div className="flex items-center gap-2" role="status" aria-live="polite">
          <Badge tone="slate">
            {matches.length} of {events.length}
          </Badge>
        </div>
      ) : null}

      <div className="animate-rise" style={stagger(2)}>
        {tournamentsQ.isLoading ? (
          // Eight, not six: the skeleton stands in for the grid below it, so it
          // fills the same two rows the results will.
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        ) : tournamentsQ.isError ? (
          <EmptyState
            title="Tournament listings are unavailable right now"
            hint="Please check back shortly."
          />
        ) : events.length === 0 ? (
          <EmptyState
            icon={<TrophyIcon className="h-5 w-5" />}
            title="No tournaments published yet"
            hint="New events will show up here as soon as organizers publish them."
          />
        ) : matches.length === 0 ? (
          <EmptyState
            icon={<SearchIcon className="h-5 w-5" />}
            title={narrowed ? "No tournaments match these filters" : "No upcoming tournaments"}
            hint={
              narrowed
                ? "Try a different state or division, or widen the dates."
                : widerWouldMatch
                  ? "Nothing is scheduled ahead, but there are past events to browse."
                  : "Check back soon for new events."
            }
            action={
              narrowed ? (
                <Button variant="secondary" onClick={clear}>
                  Clear filters
                </Button>
              ) : widerWouldMatch ? (
                <Button
                  variant="secondary"
                  onClick={() => update({ ...filters, timeframe: "all" }, sort)}
                >
                  Show all dates
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {matches.map((t, i) => (
              <div key={t.id} className="min-w-0 animate-rise" style={stagger(i)}>
                <TournamentCard t={t} terms={terms} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
